// components/iorOusCalculator/IorOusCalculatorPage.jsx
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../config/api';
import { Button, Table, message, Modal, Spin, Form, Input, Card } from 'antd';
import { useState, useEffect } from 'react';
import IorOusEstimateFormModal from './IorOusEstimateFormModal';
import IorCalculatorForm from './Web/TradeIorCalculatorForm';
import { FaRegEye, FaDownload, FaLock, FaCalculator, FaArrowLeft } from 'react-icons/fa6';
import { generateIOR } from "../shared/pdf/generateIOR";
import Logo from "../../assets/logo.png";
import { getSettings } from "../../lib/setting";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { getAllAdminUsers } from "../../lib/user";
import ExternalIorTermsPopup from "./ExternalIorTermsPopup";

function IorOusCalculatorPage() {
    const { linkId, subpage } = useParams();
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showTradeOS, setShowTradeOS] = useState(false);
    const [viewLoading, setViewLoading] = useState({});
    const [downloadLoading, setDownloadLoading] = useState({});
    const [isPdfModalVisible, setIsPdfModalVisible] = useState(false);
    const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authLoading, setAuthLoading] = useState(false);
    const [userInfo, setUserInfo] = useState(null);
    const [authToken, setAuthToken] = useState(null);
    const [form] = Form.useForm();
    const [acceptLoading, setAcceptLoading] = useState({});
    const [rejectLoading, setRejectLoading] = useState({});
    const [linkInfo, setLinkInfo] = useState(null);

    const ChatBubbleIcon = ({ className = "" }) => (
        <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            <path
                d="M4 4H20C21.1 4 22 4.9 22 6V16C22 17.1 21.1 18 20 18H6L2 22V6C2 4.9 2.9 4 4 4Z"
                fill="currentColor"
            />
            <circle cx="8" cy="12" r="1.5" fill="white" />
            <circle cx="12" cy="12" r="1.5" fill="white" />
            <circle cx="16" cy="12" r="1.5" fill="white" />
        </svg>
    );

    // Sync showTradeOS state with subpage route param
    useEffect(() => {
        setShowTradeOS(subpage === 'web_external_client_tradeos');
    }, [subpage]);

    // Fetch settings for TradeOS form
    const { data: settingsData, isLoading: settingsLoading } = useQuery(
        ["settings"],
        getSettings,
        {
            refetchOnWindowFocus: false,
        }
    );
    // Cache for estimate summaries
    const estimateSummaryCache = {};

    // Check if we have a stored token for this link
    useEffect(() => {
        const storedToken = localStorage.getItem(`ior_ous_token_${linkId}`);
        const storedExpiry = localStorage.getItem(`ior_ous_token_expiry_${linkId}`);

        if (storedToken && storedExpiry) {
            const expiryTime = parseInt(storedExpiry);
            if (Date.now() < expiryTime) {
                setAuthToken(storedToken);
                setIsAuthenticated(true);
                setupApiInterceptor(storedToken);
            } else {
                localStorage.removeItem(`ior_ous_token_${linkId}`);
                localStorage.removeItem(`ior_ous_token_expiry_${linkId}`);
            }
        }
    }, [linkId]);

    // Set up API interceptor for IOR OUS Calculator authentication
    const setupApiInterceptor = (token) => {
        const interceptor = api.interceptors.request.use(
            (config) => {
                if (config.url && config.url.includes(`/ior-ous-calculator/${linkId}`)) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        window.iorOusCalculatorInterceptor = interceptor;
    };



    // Clean up interceptor on unmount
    useEffect(() => {
        return () => {
            if (window.iorOusCalculatorInterceptor) {
                api.interceptors.request.eject(window.iorOusCalculatorInterceptor);
                delete window.iorOusCalculatorInterceptor;
            }
        };
    }, []);

    // Fetch and validate the IOR OUS user (no auth required)
    const { data: user, isLoading: userLoading, error: userError } = useQuery(
        ['iorOusUser', linkId],
        async () => {
            const res = await api.get(`/ior-ous-calculator/${linkId}`);
            setUserInfo(res.data);
            return res.data;
        },
        {
            retry: false,
            refetchOnWindowFocus: false
        }
    );

    // Fetch estimates for the user (requires auth)
    const { data: estimates, isLoading: estimatesLoading, refetch: refetchEstimates } = useQuery(
        ['iorOusEstimates', linkId],
        async () => {
            const res = await api.get(`/ior-ous-calculator/${linkId}/estimates`);
            return res.data;
        },
        {
            enabled: isAuthenticated,
            retry: false
        }
    );

    // Fetch user stats (requires auth)
    const { data: userStats, isLoading: statsLoading, refetch: refetchStats } = useQuery(
        ['iorOusUserStats', linkId],
        async () => {
            const res = await api.get(`/ior-ous-calculator/${linkId}/stats`);
            return res.data;
        },
        {
            enabled: isAuthenticated,
            retry: false,
            onSuccess: (data) => {
                if (data.userInfo) {
                    setUserInfo(prev => ({
                        ...prev,
                        ...data.userInfo,
                        companyName: data.userInfo.companyName || prev?.companyName,
                        maxEstimates: data.userInfo.maxEstimates,
                        estimatesCreated: data.userInfo.estimatesCreated
                    }));
                }
            }
        }
    );

    useEffect(() => {
        if (!showTradeOS && isAuthenticated) {
            refetchEstimates();
            refetchStats(); // Also refetch stats
        }
    }, [showTradeOS, isAuthenticated, refetchEstimates, refetchStats]);

    // Handle password authentication
    const handleAuthentication = async (values) => {
        setAuthLoading(true);
        try {
            const response = await api.post(`/ior-ous-calculator/${linkId}/authenticate`, {
                password: values.password
            });

            if (response.data.success) {
                const { token, companyName, maxEstimates, estimatesCreated } = response.data;

                const expiryTime = Date.now() + (24 * 60 * 60 * 1000);
                localStorage.setItem(`ior_ous_token_${linkId}`, token);
                localStorage.setItem(`ior_ous_token_expiry_${linkId}`, expiryTime.toString());

                setAuthToken(token);
                setIsAuthenticated(true);
                setUserInfo(prev => ({
                    ...prev,
                    companyName,
                    maxEstimates,
                    estimatesCreated
                }));

                setupApiInterceptor(token);
                message.success('Authentication successful!');
                form.resetFields();
            }
        } catch (error) {
            console.error('Authentication error:', error);
            const errorMessage = error.response?.data?.error || 'Authentication failed';
            message.error(errorMessage);
        } finally {
            setAuthLoading(false);
        }
    };

    const handleTradeOSSubmission = async () => {
        message.success('TradeOS estimate created successfully');

        // Refetch both estimates AND stats from server
        await Promise.all([refetchEstimates(), refetchStats()]);

        navigate(`/ior-ous-calculator/${linkId}`);
    };
    // Handle logout
    const handleLogout = () => {
        localStorage.removeItem(`ior_ous_token_${linkId}`);
        localStorage.removeItem(`ior_ous_token_expiry_${linkId}`);
        setAuthToken(null);
        setIsAuthenticated(false);
        setUserInfo(prev => prev ? { ...prev, requiresAuth: true } : null);

        if (window.iorOusCalculatorInterceptor) {
            api.interceptors.request.eject(window.iorOusCalculatorInterceptor);
            delete window.iorOusCalculatorInterceptor;
        }

        message.info('Logged out successfully');
    };

    // Fetch estimate summary
    const fetchEstimateSummary = async (estimateId) => {
        if (estimateSummaryCache[estimateId]) {
            return estimateSummaryCache[estimateId];
        }

        try {
            const response = await api.get(`/ior-ous-calculator/${linkId}/estimates/${estimateId}/summary`);
            estimateSummaryCache[estimateId] = response.data.estimateSummary;
            return response.data.estimateSummary;
        } catch (error) {
            console.error("Error fetching estimate summary:", error);
            message.error("Error fetching estimate summary");
            return null;
        }
    };

    // Handle successful estimate submission
    const handleEstimateSubmission = () => {
        message.success('Estimate created successfully');
        refetchEstimates();
        setIsModalOpen(false);
        if (userInfo) {
            setUserInfo(prev => ({
                ...prev,
                estimatesCreated: (prev.estimatesCreated || 0) + 1
            }));
        }
    };

    // Handle View PDF
    const handleViewPdf = async (estimate) => {
        setViewLoading(prev => ({ ...prev, [estimate._id]: true }));
        try {
            const estimateSummary = await fetchEstimateSummary(estimate._id);
            if (!estimateSummary) {
                throw new Error("Failed to fetch estimate summary");
            }

            // NEW: Filter out zero or invalid services (TradeOS + Old format safe)
            const validServices = (estimateSummary.services || []).filter(service => {
                const amount = service.total ?? service.value ?? service.exclVAT ?? 0;
                return parseFloat(amount) > 0;
            });

            // Recalculate total from valid services only
            let servicesTotal = 0;
            validServices.forEach(s => {
                const amount = s.total ?? s.value ?? s.exclVAT ?? 0;
                servicesTotal += parseFloat(amount) || 0;
            });

            // Add DHL taxes (VAT, etc.)
            let taxesTotal = 0;
            if (estimateSummary.dhlTaxes) {
                estimateSummary.dhlTaxes.forEach(tax => {
                    if (tax.value) taxesTotal += parseFloat(tax.value) || 0;
                    if (tax.details) {
                        tax.details.forEach(d => taxesTotal += parseFloat(d.value) || 0);
                    }
                });
            }

            const finalTotal = servicesTotal + taxesTotal;
            if (finalTotal === 0 && estimateSummary.total) {
                // Fallback only if nothing found
                servicesTotal = parseFloat(estimateSummary.total) || 0;
            }

            const modifiedDhlEstimation = estimate.dhlEstimation
                ? {
                    ...estimate.dhlEstimation,
                    feeTotals: estimateSummary.dhlTaxes
                        ? estimateSummary.dhlTaxes
                            .filter(tax => {
                                if (tax.value) return parseFloat(tax.value) > 0;
                                if (tax.details) return tax.details.some(d => parseFloat(d.value) > 0);
                                return false;
                            })
                            .map(tax => ({
                                name: tax.name,
                                currency: estimateSummary.currency,
                                value: tax.value || (tax.details?.reduce((sum, d) => sum + (parseFloat(d.value) || 0), 0)) || 0,
                            }))
                        : [],
                }
                : null;

            // Block PDF if no real charges
            if (validServices.length === 0 && !modifiedDhlEstimation?.feeTotals?.length) {
                message.warn("No chargeable services found. PDF will be empty.");
                setViewLoading(prev => ({ ...prev, [estimate._id]: false }));
                return;
            }

            // Pass clean data to PDF generator
            const pdfBlob = await generateIOR(
                estimate,
                {
                    ...estimateSummary,
                    services: validServices, // Only real services
                    total: finalTotal.toFixed(2),
                    currency: estimateSummary.currency || estimate.itemsCurrency || 'USD'
                },
                modifiedDhlEstimation
            );

            const pdfUrl = URL.createObjectURL(pdfBlob);
            setCurrentPdfUrl(pdfUrl);
            setIsPdfModalVisible(true);
        } catch (error) {
            console.error('Error generating PDF:', error);
            message.error(`Error generating PDF: ${error.message || 'Unknown error'}`);
        } finally {
            setViewLoading(prev => ({ ...prev, [estimate._id]: false }));
        }
    };

    // Handle Download PDF
    const handleDownloadPdf = async (estimate) => {
        setDownloadLoading(prev => ({ ...prev, [estimate._id]: true }));
        try {
            const estimateSummary = await fetchEstimateSummary(estimate._id);
            if (!estimateSummary) {
                throw new Error("Failed to fetch estimate summary");
            }

            // Same filtering logic as View PDF
            const validServices = (estimateSummary.services || []).filter(service => {
                const amount = service.total ?? service.value ?? service.exclVAT ?? 0;
                return parseFloat(amount) > 0;
            });

            let servicesTotal = 0;
            validServices.forEach(s => {
                const amount = s.total ?? s.value ?? s.exclVAT ?? 0;
                servicesTotal += parseFloat(amount) || 0;
            });

            let taxesTotal = 0;
            if (estimateSummary.dhlTaxes) {
                estimateSummary.dhlTaxes.forEach(tax => {
                    if (tax.value) taxesTotal += parseFloat(tax.value) || 0;
                    if (tax.details) {
                        tax.details.forEach(d => taxesTotal += parseFloat(d.value) || 0);
                    }
                });
            }

            const finalTotal = servicesTotal + taxesTotal || parseFloat(estimateSummary.total) || 0;

            const modifiedDhlEstimation = estimate.dhlEstimation
                ? {
                    ...estimate.dhlEstimation,
                    feeTotals: estimateSummary.dhlTaxes
                        ? estimateSummary.dhlTaxes
                            .filter(tax => {
                                if (tax.value) return parseFloat(tax.value) > 0;
                                if (tax.details) return tax.details.some(d => parseFloat(d.value) > 0);
                                return false;
                            })
                            .map(tax => ({
                                name: tax.name,
                                currency: estimateSummary.currency,
                                value: tax.value || (tax.details?.reduce((sum, d) => sum + (parseFloat(d.value) || 0), 0)) || 0,
                            }))
                        : [],
                }
                : null;

            if (validServices.length === 0 && !modifiedDhlEstimation?.feeTotals?.length) {
                message.warn("No chargeable services found. Cannot download empty PDF.");
                setDownloadLoading(prev => ({ ...prev, [estimate._id]: false }));
                return;
            }

            const pdfBlob = await generateIOR(
                estimate,
                {
                    ...estimateSummary,
                    services: validServices,
                    total: finalTotal.toFixed(2),
                    currency: estimateSummary.currency || estimate.itemsCurrency || 'USD'
                },
                modifiedDhlEstimation
            );

            const pdfUrl = URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.download = `ior-ous-estimate_${estimate.estimateId || estimate._id}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(pdfUrl);
        } catch (error) {
            console.error('Error downloading PDF:', error);
            message.error(`Error downloading PDF: ${error.message || 'Unknown error'}`);
        } finally {
            setDownloadLoading(prev => ({ ...prev, [estimate._id]: false }));
        }
    };

    // Handle Accept Estimate
    const handleAcceptEstimate = async (estimate) => {
        setAcceptLoading((prev) => ({ ...prev, [estimate._id]: true }));
        try {
            // 1. Get admin emails
            const admins = await getAllAdminUsers();
            const adminEmails = admins
                .map((admin) => admin.email)
                .filter((email) => email);

            // Ensure linkInfo is loaded
            let companyName = "External Guest User";

            if (linkId) {
                try {
                    const res = await api.get(`/ior-ous-calculator/${linkId}`);
                    companyName = res.data.companyName || "External Guest User";
                } catch (err) {
                }
            }

            // 2. Send email notification only when recipients exist
            if (adminEmails.length) {
                await api.post('/mail/send-external-accept-notification', {
                    estimateData: estimate,
                    adminEmails: adminEmails,
                    linkName: companyName || "External Link"
                });
            }

            // 3. Notify backend that estimate was accepted 
            await api.post(`/ior-ous-calculator/${linkId}/estimates/${estimate._id}/accept-notify`);

            if (adminEmails.length) {
                message.success("Estimate accepted and admins notified!");
            } else {
                message.warning("Estimate accepted and admin notification created, but no admin email recipients were found.");
            }
            refetchEstimates();
        } catch (error) {
            console.error("Error accepting estimate:", error);
            message.error(
                "Failed to process acceptance: " + (error.response?.data?.message || error.message)
            );
        } finally {
            setAcceptLoading((prev) => ({ ...prev, [estimate._id]: false }));
        }
    };

    const handleRejectEstimate = async (estimate) => {
        setRejectLoading((prev) => ({ ...prev, [estimate._id]: true }));
        try {
            await api.post(`/ior-ous-calculator/${linkId}/estimates/${estimate._id}/reject-notify`);
            message.success("Rejection notification sent to Admins.");
            refetchEstimates();
        } catch (error) {
            console.error("Error rejecting estimate:", error);
            message.error(
                "Failed to process rejection: " + (error.response?.data?.message || error.message)
            );
        } finally {
            setRejectLoading((prev) => ({ ...prev, [estimate._id]: false }));
        }
    };

    const handleChatClick = () => {
        message.info("Chat feature will be added here.");
    };

    // Check if user can create more estimates
    const canCreateEstimate = () => {
        if (!userInfo) return false;
        return (userInfo.estimatesCreated || 0) < (userInfo.maxEstimates || 3);
    };

    // Calculate remaining estimates for display
    const getRemainingEstimates = () => {
        if (!userInfo) return 0;
        return Math.max(0, (userInfo.maxEstimates || 3) - (userInfo.estimatesCreated || 0));
    };

    if (userLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <Spin size="large" />
            </div>
        );
    }

    if (userError || !user?.success) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <Card className="max-w-md w-full">
                    <div className="text-center">
                        <FaLock className="text-6xl text-red-500 mx-auto mb-4" />
                        <h2 className="text-xl font-bold text-gray-800 mb-2">Invalid Access</h2>
                        <p className="text-gray-600">
                            This IOR & EOR Calculator access is invalid, expired, or has been disabled.
                        </p>
                    </div>
                </Card>
            </div>
        );
    }

    // Show authentication form if not authenticated
    if (!isAuthenticated) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-50">
                <Card className="max-w-md w-full">
                    <div className="text-center mb-6">
                        <div className="flex justify-start mb-6">
                            <img className="w-32" src={Logo} alt="logo" />
                        </div>
                        <FaCalculator className="text-4xl text-blue-600 mx-auto mb-4" />
                        <h2 className="text-xl font-bold text-gray-800 mb-2">
                            IOR & EOR Calculator
                        </h2>
                        <p className="text-sm text-gray-600 mb-2">
                            {userInfo?.companyName || 'Secure Access Required'}
                        </p>
                        <p className="text-gray-600">
                            Please enter your password to access the calculator.
                        </p>
                    </div>

                    <Form
                        form={form}
                        onFinish={handleAuthentication}
                        layout="vertical"
                    >
                        <Form.Item
                            name="password"
                            label="Access Password"
                            rules={[{ required: true, message: 'Please enter the password' }]}
                        >
                            <Input.Password
                                placeholder="Enter your password"
                                size="large"
                            />
                        </Form.Item>

                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={authLoading}
                                size="large"
                                className="w-full bg-blue-600"
                            >
                                Access Calculator
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            </div>
        );
    }

    const columns = [
        {
            title: 'Estimate ID',
            dataIndex: 'estimateId',
            key: 'estimateId',
            align: "center",
        },
        {
            title: 'User Name',
            dataIndex: 'userName',
            key: 'userName',
            align: "center",
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            align: "center",
        },
        {
            title: 'Service',
            dataIndex: 'selectedService',
            key: 'selectedService',
            align: "center",
        },
        {
            title: 'Route',
            key: 'route',
            align: "center",
            render: (text, record) => `${record.originCountry} → ${record.destinationCountry}`,
        },
        {
            title: 'Creation Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            align: "center",
            render: (text) => new Date(text).toLocaleDateString(),
        },
        {
            title: 'Actions',
            key: 'actions',
            align: "center",
            render: (text, record) => (
                <div className="flex justify-center gap-3">
                    {(() => {
                        const responseStatus = record.customerResponseStatus || (record.estimationAccept ? "accepted" : "pending");
                        const hasResponded = responseStatus === "accepted" || responseStatus === "rejected";

                        return (
                            <>
                                <button
                                    onClick={() => handleAcceptEstimate(record)}
                                    className="py-1 px-3 rounded-md bg-[#7CE13E] text-white hover:bg-green-600 transition-colors disabled:cursor-not-allowed"
                                    title="Accept Estimate & Notify Admins"
                                    disabled={acceptLoading[record._id] || hasResponded}
                                >
                                    {acceptLoading[record._id] ? (
                                        <Spin size="small" />
                                    ) : (
                                        <FaCheckCircle className="text-2xl" />
                                    )}
                                </button>
                                <button
                                    onClick={() => handleRejectEstimate(record)}
                                    className="py-1 px-3 rounded-md bg-[#EF4444] text-white hover:bg-red-600 transition-colors disabled:cursor-not-allowed"
                                    title="Reject Estimate & Notify Admins"
                                    disabled={rejectLoading[record._id] || hasResponded}
                                >
                                    {rejectLoading[record._id] ? (
                                        <Spin size="small" />
                                    ) : (
                                        <FaTimesCircle className="text-2xl" />
                                    )}
                                </button>
                                <button
                                    onClick={handleChatClick}
                                    className="py-1 px-3 rounded-md bg-[#A9A9A9] text-white hover:bg-gray-600 transition-colors"
                                    title="Chat"
                                >
                                    <ChatBubbleIcon className="h-6 w-6" />
                                </button>
                            </>
                        );
                    })()}
                    <button
                        onClick={() => handleViewPdf(record)}
                        className="py-1 px-3 rounded-md bg-[#A9A9A9] text-white hover:bg-gray-600 transition-colors"
                        disabled={viewLoading[record._id]}
                        title="View PDF"
                    >
                        {viewLoading[record._id] ? (
                            <Spin size="small" />
                        ) : (
                            <FaRegEye className="text-2xl" />
                        )}
                    </button>
                    <button
                        onClick={() => handleDownloadPdf(record)}
                        className="py-1 px-3 rounded-md bg-[#3861DC] text-white hover:bg-blue-700 transition-colors"
                        disabled={downloadLoading[record._id]}
                        title="Download PDF"
                    >
                        {downloadLoading[record._id] ? (
                            <Spin size="small" />
                        ) : (
                            <FaDownload className="text-2xl" />
                        )}
                    </button>
                </div>
            ),
        },
    ];

    // If TradeOS is active (based on route), render TradeOS view
    if (showTradeOS) {
        return (
            <div className="flex flex-col min-h-screen bg-gray-50">
                <ExternalIorTermsPopup linkId={linkId} onLogout={handleLogout} />
                <div className="flex justify-start p-4 pt-2">
                    <img className="w-32" src={Logo} alt="logo" />
                </div>

                <div className="flex-1 p-4 overflow-auto">
                    <IorCalculatorForm
                        settingsData={settingsData}
                        settingsLoading={settingsLoading}
                        isGuest={true}
                        linkId={linkId}
                        onSubmit={handleTradeOSSubmission}
                        renderBackButton={(show) => (
                            show && (
                                <div className="mb-6 flex justify-start pl-12">
                                    <Button
                                        onClick={() => navigate(`/ior-ous-calculator/${linkId}`)}
                                        className="btnBg text-white flex items-center gap-2"
                                        icon={<FaArrowLeft />}
                                    >
                                        Back
                                    </Button>
                                </div>
                            )
                        )}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4">
            <ExternalIorTermsPopup linkId={linkId} onLogout={handleLogout} />
            <div className="flex justify-start mb-6">
                <img className="w-32" src={Logo} alt="logo" />
            </div>

            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 mb-2 flex items-center gap-3">
                            <FaCalculator className="text-blue-600" />
                            IOR & EOR Calculator
                        </h1>
                        <p className="text-lg text-gray-700 mb-2">
                            Welcome, {userInfo?.companyName}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                            {statsLoading ? (
                                <Spin size="small" />
                            ) : (
                                <>
                                    <span>
                                        Estimates: {userInfo?.estimatesCreated || 0} / {userInfo?.maxEstimates || 3}
                                    </span>
                                    <span>
                                        Remaining: {getRemainingEstimates()}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-3">
                        {/* <Button
                            className="btnBg text-white flex items-center gap-2"
                            onClick={() => setIsModalOpen(true)}
                            disabled={!canCreateEstimate() || statsLoading}
                            title={!canCreateEstimate() ? "Maximum estimates reached" : "Create new estimate"}
                        >
                            <FaCalculator />
                            Calculate IOR & EOR
                        </Button> */}
                        <Button
                            className="btnBg text-white"
                            onClick={() => navigate(`/ior-ous-calculator/${linkId}/web_external_client_tradeos`)}
                            disabled={!canCreateEstimate() || statsLoading || settingsLoading}
                            title={!canCreateEstimate() ? "Maximum estimates reached" : "Open TradeOS"}
                        >
                            TradeOS
                        </Button>
                        <Button
                            onClick={handleLogout}
                            className="text-gray-600 border-gray-300"
                        >
                            Logout
                        </Button>
                    </div>
                </div>
            </div>

            {/* Information Panel */}
            <div className="bg-blue-50 border-l-4 border-blue-400 text-blue-700 p-4 mb-6 rounded-md shadow-sm">
                <div className="flex items-start">
                    <FaCalculator className="text-2xl mr-3 mt-1" />
                    <div>
                        <p className="font-semibold">IOR & EOR Calculator</p>
                        <p className="text-sm">
                            Calculate duties, taxes, and fees for importing/exporting goods.
                            Get accurate estimates for your IOR (Importer of Record) and EOR (Exporter of Record) requirements.
                        </p>
                    </div>
                </div>
            </div>

            {/* Estimates Table */}
            <div className="bg-white rounded-lg shadow-sm">
                <Table
                    columns={columns}
                    dataSource={estimates}
                    loading={estimatesLoading}
                    rowKey="_id"
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} estimates`
                    }}
                />
            </div>

            {/* Estimate Form Modal */}
            {isModalOpen && (
                <IorOusEstimateFormModal
                    isVisible={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSubmit={handleEstimateSubmission}
                    isGuest={true}
                    linkId={linkId}
                    isModal={true}
                />
            )}

            {/* PDF Preview Modal */}
            {isPdfModalVisible && (
                <Modal
                    title="Estimate Preview"
                    open={isPdfModalVisible}
                    onCancel={() => {
                        setIsPdfModalVisible(false);
                        if (currentPdfUrl) {
                            URL.revokeObjectURL(currentPdfUrl);
                            setCurrentPdfUrl(null);
                        }
                    }}
                    footer={null}
                    width="80%"
                    style={{ top: 20 }}
                >
                    <iframe
                        src={`${currentPdfUrl}#toolbar=0`}
                        title="Estimate PDF"
                        style={{ width: '100%', height: '70vh', border: 0 }}
                    />
                </Modal>
            )}
        </div>
    );
}

export default IorOusCalculatorPage;
